import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { queueCount, flushQueue } from "../lib/endorsementQueue";

export default function EndorsementSyncManager() {
  const [count, setCount] = useState(() => queueCount());
  const [syncing, setSyncing] = useState(false);

  const runFlush = useCallback(async (includeFailed = false) => {
    if (queueCount() === 0 || !navigator.onLine) return;
    setSyncing(true);
    const { ok, failed } = await flushQueue({ includeFailed });
    setSyncing(false);
    setCount(queueCount());
    if (ok > 0) toast.success(`${ok} queued endorsement${ok > 1 ? "s" : ""} submitted`);
    if (failed > 0) toast.error(`${failed} queued endorsement${failed > 1 ? "s" : ""} were rejected — tap the badge to review/retry`);
  }, []);

  useEffect(() => {
    const onChange = (e) => setCount(e.detail?.count ?? queueCount());
    const onOnline = () => runFlush(false);
    window.addEventListener("endorsement-queue-changed", onChange);
    window.addEventListener("online", onOnline);
    // Attempt a flush on mount (e.g. page opened already online with pending items)
    const t = setTimeout(() => runFlush(false), 1200);
    return () => {
      window.removeEventListener("endorsement-queue-changed", onChange);
      window.removeEventListener("online", onOnline);
      clearTimeout(t);
    };
  }, [runFlush]);

  if (count === 0) return null;

  return (
    <button
      onClick={() => runFlush(true)}
      disabled={syncing}
      className="fixed bottom-6 left-4 z-[9998] flex items-center gap-2 rounded-full bg-stone-800 text-white shadow-lg px-4 py-2 text-xs sm:text-sm font-medium hover:bg-stone-700 transition-colors disabled:opacity-70"
      data-testid="endorsement-sync-badge"
      title="Pending endorsements waiting to sync. Tap to retry now."
    >
      <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
      {syncing ? "Syncing…" : `${count} pending sync`}
    </button>
  );
}
